/**
 * Billing: a business's subscription row is read straight from PocketBase (its members may read
 * it; only services/billing writes it). Checkout and the customer portal go through the service,
 * which holds the Stripe key. What a plan allows is `lib/plan/plan.ts`.
 */
import { ClientResponseError } from "pocketbase";
import { env } from "@/lib/platform/env";
import type { Interval, PlanId } from "@/lib/plan/plan";
import type { SubscriptionsResponse } from "@/lib/platform/pb-types";
import { pb } from "@/lib/platform/pocketbase";

export type Subscription = SubscriptionsResponse;
export type Plan = {
  id: PlanId;
  name: string;
  /** Net amounts in cents; `configured` is false when the Stripe price id is missing. */
  prices: Record<Interval, { amount: number; configured: boolean }>;
  /** Plus for a further business of an owner who already pays Plus (Checkout applies it). */
  discountPercent?: number;
};

export async function getWorkspaceSubscription(workspaceId: string): Promise<Subscription | null> {
  try {
    return await pb
      .collection("subscriptions")
      .getFirstListItem(pb.filter("workspace = {:workspaceId}", { workspaceId }));
  } catch (error) {
    if (error instanceof ClientResponseError && error.status === 404) return null;
    throw error;
  }
}

function headers(): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${pb.authStore.token}`,
  };
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // ignore, fall through
  }
  return `Billing request failed (${res.status})`;
}

/** The plans and prices; for a business, with the discount its owner would get. */
export async function listPlans(workspace?: string): Promise<Plan[]> {
  const query = workspace ? `?${new URLSearchParams({ workspace })}` : "";
  const res = await fetch(`${env.billingUrl}/plans${query}`, { headers: headers() });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { plans: Plan[] };
  return body.plans;
}

/** Returns the Checkout URL to redirect the browser to. Only the owner may call it. */
export async function createCheckout(input: {
  workspace: string;
  plan: PlanId;
  interval: Interval;
}): Promise<string> {
  const res = await fetch(`${env.billingUrl}/checkout`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { url: string };
  return body.url;
}

/** Returns the customer portal URL (change plan, payment method, invoices, cancel). */
export async function createPortal(workspace: string): Promise<string> {
  const res = await fetch(`${env.billingUrl}/portal`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ workspace }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { url: string };
  return body.url;
}
